import type { ResultsApi } from "@/v2/api/metadata/results";
import type { SportsApi } from "@/v2/api/metadata/sports";
import type { BackofficeContext } from "@/v2/framework/context/types";
import type { DialogConfig } from "@/v2/framework/dialog/types";
import { buildAddSupplementalNamesPayload } from "@/v2/kits/supplemental-names";
import {
  sportDefaultEventSelectionOptions,
  sportGamesStatusOptions,
  sportPositionValidator,
  sportStatusOptions,
  sportUsViewEventRowLayoutOptions,
  US_VIEW_EVENT_ROW_LAYOUT_ID,
} from "../../shared";

const displayIdChildren = [
  {
    type: "text-field" as const,
    name: "prefix",
    label: "",
    placeholder: "Prefix",
    flex: 1,
  },
  {
    type: "text-field" as const,
    name: "rangeFrom",
    label: "",
    placeholder: "From",
    inputType: "number" as const,
    flex: 1,
  },
  {
    type: "text-field" as const,
    name: "rangeTo",
    label: "",
    placeholder: "To",
    inputType: "number" as const,
    flex: 1,
  },
];

export function createAddDialog(options: {
  ctx: BackofficeContext;
  sportsApi: SportsApi;
  resultsApi: ResultsApi;
}): DialogConfig {
  const { ctx, sportsApi, resultsApi } = options;

  return {
    id: "add-sport",
    title: "Add Sport",
    trigger: { type: "fab" },
    layout: { type: "popover" },
    structure: {
      type: "stepper",
      steps: [
        {
          id: "main",
          children: [
            {
              type: "group",
              name: "main",
              label: "Main",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  placeholder: "Select status",
                  options: sportStatusOptions,
                  defaultValue: 1,
                },
                {
                  type: "text-field",
                  name: "name",
                  label: "Name",
                  required: true,
                  placeholder: "Enter a name",
                },
                {
                  type: "text-field",
                  name: "info",
                  label: "Info",
                  placeholder: "Enter info",
                },
                {
                  type: "text-field",
                  name: "code",
                  label: "Code",
                  placeholder: "Enter code",
                },
                {
                  type: "component",
                  component: () =>
                    import("@/v2/kits/supplemental-names/components/SupplementalNamesFields.vue"),
                  props: { mode: "add", entity: "SPORT" },
                },
                {
                  type: "select-field",
                  name: "applicationConfiguration",
                  label: "sport_metadata.us_view_event_row_layout.label",
                  placeholder: "Select layout",
                  options: sportUsViewEventRowLayoutOptions,
                  defaultValue: -1,
                },
              ],
            },
            {
              type: "component",
              component: () => import("./results/SportResultsMappingFields.vue"),
              props: { mode: "add", sportsApi, resultsApi },
            },
            {
              type: "group",
              name: "prematchGame",
              label: "Pre-match",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  placeholder: "Select pre-match status",
                  required: true,
                  options: sportGamesStatusOptions,
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  placeholder: "Enter position",
                  validate: sportPositionValidator,
                },
                {
                  type: "structured-array-field",
                  name: "displayIds",
                  label: "Display IDs",
                  minItems: 0,
                  maxItems: 50,
                  children: displayIdChildren,
                },
                {
                  type: "select-field",
                  name: "defaultEventSelection",
                  label: "Default Event Selection",
                  placeholder: "Select default event selection for pre-match game",
                  options: sportDefaultEventSelectionOptions,
                },
              ],
            },
            {
              type: "group",
              name: "liveGame",
              label: "Live",
              children: [
                {
                  type: "select-field",
                  name: "status",
                  label: "Status",
                  required: true,
                  options: sportGamesStatusOptions,
                  placeholder: "Select live status",
                },
                {
                  type: "text-field",
                  name: "position",
                  label: "Position",
                  placeholder: "Enter position",
                  validate: sportPositionValidator,
                },
                {
                  type: "structured-array-field",
                  name: "displayIds",
                  label: "Display IDs",
                  minItems: 0,
                  maxItems: 50,
                  children: displayIdChildren,
                },
                {
                  type: "select-field",
                  name: "defaultEventSelection",
                  label: "Default Event Selection",
                  placeholder: "Select default event selection for live game",
                  options: sportDefaultEventSelectionOptions,
                },
              ],
            },
          ],
        },
      ],
    },
    api: {
      submit: (data) => {
        const main = (data.main ?? {}) as Record<string, unknown>;
        const prematchGame = (data.prematchGame ?? {}) as Record<string, unknown>;
        const liveGame = (data.liveGame ?? {}) as Record<string, unknown>;
        const results = Array.isArray(data.results) ? data.results : [];
        const applicationId = ctx.read.applicationId();
        const layoutValue = main.applicationConfiguration;
        const model: Record<string, unknown> = {
          status: main.status,
          name: main.name,
          info: main.info,
          code: main.code,
          supplementalNames: buildAddSupplementalNamesPayload(data, "SPORT"),
          prematchGame: {
            status: prematchGame.status,
            position: prematchGame.position,
            displayIds: prematchGame.displayIds,
            defaultEventSelection: prematchGame.defaultEventSelection,
          },
          liveGame: {
            status: liveGame.status,
            position: liveGame.position,
            displayIds: liveGame.displayIds,
            defaultEventSelection: liveGame.defaultEventSelection,
          },
          results: results
            .filter((row) => row && typeof row === "object" && (row as Record<string, unknown>).resultId)
            .map((row) => {
              const result = row as Record<string, unknown>;
              return {
                resultId: result.resultId,
                status: result.status ?? 1,
                position: result.position,
              };
            }),
        };

        if (applicationId && layoutValue != null && layoutValue !== "" && layoutValue !== -1) {
          model.applicationConfigurations = [
            {
              applicationId,
              configurationId: US_VIEW_EVENT_ROW_LAYOUT_ID,
              configurationValue: layoutValue,
            },
          ];
        }

        return sportsApi.create(model);
      },
    },
  };
}
